// Автогенерация ДКП теперь идёт через Edge Function `generate-dkp`:
//   • она авторизует пользователя по JWT (inspector/manager заказа,
//     admin, dispatcher);
//   • держит ключ pdfgen (X-Api-Key) в секрете Supabase, а не в публичном
//     бандле админки;
//   • проксирует POST /api/v1/render-order и возвращает PDF + заголовки
//     X-Document-Id / X-Pdf-Path.
//
// Раньше тут был прямой `fetch` в pdfgen с `VITE_PDF_GENERATOR_API_KEY` —
// этот секрет уходил в production-bundle. Удалили.
//
// Скачивание ранее сгенерированного PDF и метаданные `order_documents`
// продолжаем читать напрямую через supabase-js (RLS пускает admin/dispatcher
// и пары менеджер/инспектор заказа).

import { SUPABASE_ANON_KEY, SUPABASE_URL, USE_MOCK } from '@/config'
import i18n from '@/utils/i18n'
import { createClient } from '@/api/supabase'

/**
 * Автогенерация ДКП по заказу.
 *
 * Возвращает blob + метаданные сохранённого документа (id записи в
 * `order_documents`, путь к PDF в bucket `documents`).
 */
export interface GeneratedDkp {
  pdf: Blob
  documentId: string
  pdfPath: string
}

export async function generateOrderDkp(orderId: string): Promise<GeneratedDkp> {
  if (USE_MOCK) {
    // В моках возвращаем «фейковый» PDF, чтобы UI работал.
    const blob = new Blob(['%PDF-MOCK\nGenerated for ' + orderId], { type: 'application/pdf' })
    return { pdf: blob, documentId: `mock-${orderId}`, pdfPath: `orders/${orderId}/dkp_mock.pdf` }
  }

  const supabase = createClient()
  const { data: sessionData } = await supabase.auth.getSession()
  const accessToken = sessionData.session?.access_token
  if (!accessToken) {
    throw new Error(i18n.t('apiErr.dkp.sessionExpired'))
  }

  // Идём в обход supabase.functions.invoke: тот пытается декодить тело
  // как JSON/utf8, и application/pdf приходит порченым. Прямой fetch
  // отдаёт Blob как есть.
  const res = await fetch(`${SUPABASE_URL}/functions/v1/generate-dkp`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      order_id: orderId,
      document_type: 'dkp',
    }),
  })

  if (!res.ok) {
    // Тело — JSON-ошибка от edge или текст от pdfgen.
    let detail = `${res.status}`
    try {
      const j = await res.clone().json()
      detail = j.detail ?? j.error ?? detail
    } catch {
      try {
        detail = await res.text()
      } catch {
        /* keep status */
      }
    }
    throw new Error(i18n.t('apiErr.dkp.generateFailed', { detail }))
  }

  const documentId = res.headers.get('X-Document-Id') ?? ''
  const pdfPath = res.headers.get('X-Pdf-Path') ?? ''
  const pdf = await res.blob()
  return { pdf, documentId, pdfPath }
}

/** Запись в таблице `order_documents` — текущий документ заказа указанного типа. */
export interface OrderDocument {
  id: string
  order_id: string
  type: 'dkp' | 'attorney' | 'acceptance_act'
  contract_num: string | null
  contract_date: string | null
  pdf_path: string
  source_template_path: string | null
  generated_at: string
  generated_by: string | null
  is_current: boolean
  created_at: string
}

/**
 * Достать «текущий» ДКП заказа (последнюю автогенерацию). null если ни одной.
 */
export async function getLatestDkpDocument(orderId: string): Promise<OrderDocument | null> {
  if (USE_MOCK) return null

  const supabase = createClient()
  const { data, error } = await supabase
    .from('order_documents')
    .select('*')
    .eq('order_id', orderId)
    .eq('type', 'dkp')
    .eq('is_current', true)
    .maybeSingle()

  if (error) {
    // PGRST116 = «no rows» в single-режиме; для maybeSingle он не должен прилетать,
    // но на всякий случай не валим UI.
    if (error.code === 'PGRST116') return null
    throw new Error(error.message)
  }
  return (data as OrderDocument) ?? null
}

/** Signed URL для скачивания PDF из bucket `documents`. */
export async function getDkpDocumentSignedUrl(
  pdfPath: string,
  expiresInSec = 600,
): Promise<string | null> {
  if (USE_MOCK) return null
  if (!pdfPath) return null
  const supabase = createClient()
  const { data, error } = await supabase.storage
    .from('documents')
    .createSignedUrl(pdfPath, expiresInSec)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}
